/**
 * HUD + screen controller. The in-game HUD writes happen at ~60 Hz from the
 * game loop, so we skip any reactive layer and just touch DOM nodes directly.
 *
 * The game drops the player straight into `playing`. Overlays (`paused`,
 * `gameover`, `leaderboard`) sit on top of the canvas; the leaderboard has
 * no home of its own, it remembers the screen it was opened from and returns
 * there on close.
 */

export type Screen = "playing" | "paused" | "gameover" | "leaderboard";

export interface GameSnapshot {
  score: number;
  hunger: number;
  stamina: number;
  staminaMax: number;
  level: number;
  airborne: boolean;
  elapsedMs: number;
  items: readonly { id: string; name: string }[];
  thirdPerson: boolean;
  lastEvent: string;
  /** When set with `lastEvent`, the HUD shows a food thumbnail and rise/fade animation. */
  lastEventFood: null | { id: string; name: string; color: number };
  /** Optional left-side icon for non-food event banners. */
  lastEventIcon: string | null;
  /** Increments whenever a new banner is shown so the HUD can restart animations. */
  eventBannerSeq: number;
  /** If true, keep event panel static (no rise/fade animation). */
  eventBannerPinned: boolean;
  challengeActive: boolean;
  challengeTitle: string;
  challengeProgress: number;
  challengeGoal: number;
  challengeChecklist: null | {
    item1Label: string;
    item1Done: boolean;
    item2Label?: string;
    item2Done?: boolean;
  };
  challengeFlashSeq: number;
  challengeCompletionFood: { id: string; name: string; color: number };
  challengeCompletionPoints: number;
}

export interface RunSummary {
  score: number;
  durationMs: number;
  itemsCollected: number;
  reason: string;
}

export interface HudBinding {
  setScreen(screen: Screen): void;
  getScreen(): Screen;
  setPointerLocked(locked: boolean): void;
  updateGameState(s: GameSnapshot): void;
  showRunSummary(summary: RunSummary): void;
  setSubmitStatus(status: "idle" | "submitting" | "ok" | "error"): void;
  getPlayerName(): string;
  setPlayerName(name: string): void;
  showChallengeDialog(show: boolean, kind?: "grandma" | "donutHeist" | "polly"): void;
  onChallengeChoice(handler: (accepted: boolean) => void): void;
  onAction(handler: (action: string) => void): void;
  onSubmit(handler: () => void): void;
}

export function createHud(): HudBinding {
  const body = document.body;
  const $ = (sel: string): HTMLElement | null => document.querySelector(sel);
  const binds: Record<string, HTMLElement[]> = {};
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("[data-bind]"))) {
    const key = el.dataset["bind"];
    if (!key) continue;
    (binds[key] ??= []).push(el);
  }

  function setBind(key: string, value: string | number): void {
    const nodes = binds[key];
    if (!nodes) return;
    for (const el of nodes) {
      if (el instanceof HTMLProgressElement) el.value = Number(value);
      else el.textContent = String(value);
    }
  }

  let currentScreen: Screen = "playing";
  function setScreen(screen: Screen): void {
    currentScreen = screen;
    body.dataset["screen"] = screen;
  }
  setScreen("playing");

  const actionHandlers: Array<(action: string) => void> = [];
  const submitHandlers: Array<() => void> = [];
  const challengeChoiceHandlers: Array<(accepted: boolean) => void> = [];

  document.addEventListener("click", (evt) => {
    const target = evt.target;
    if (!(target instanceof HTMLElement)) return;
    const btn = target.closest<HTMLElement>("[data-action]");
    if (!btn) return;
    const action = btn.dataset["action"];
    if (!action) return;
    for (const h of actionHandlers) h(action);
  });

  const submitForm = $("#submit-form") as HTMLFormElement | null;
  submitForm?.addEventListener("submit", (evt) => {
    evt.preventDefault();
    for (const h of submitHandlers) h();
  });

  const playerInput = $("#player-name") as HTMLInputElement | null;

  const hudEventRoot = $("#hud-event");
  const hudEventPanel = $("#hud-event-panel");
  const hudEventText = $("#hud-event-text");
  const hudEventThumb = $("#hud-event-thumb") as HTMLImageElement | null;
  const hudChallenge = $("#hud-challenge");
  const hudChallengeTitle = $("#hud-challenge-title");
  const hudChallengeProgress = $("#hud-challenge-progress");
  const hudChallengeChecklist = $("#hud-challenge-checklist");
  const hudChallengeItem1 = $("#hud-challenge-item-1");
  const hudChallengeItem2 = $("#hud-challenge-item-2");
  const challengeDialog = $("#challenge-dialog");
  const challengeDialogThumb = $("#challenge-dialog-thumb") as HTMLImageElement | null;
  const challengeYesBtn = $("#challenge-yes") as HTMLButtonElement | null;
  const challengeNoBtn = $("#challenge-no") as HTMLButtonElement | null;
  const challengeCompleteDialog = $("#challenge-complete-dialog");
  const challengeCompleteThumb = $("#challenge-complete-thumb") as HTMLImageElement | null;
  const challengeCompleteOkBtn = $("#challenge-complete-ok") as HTMLButtonElement | null;
  let lastEventBannerSeq = -1;
  let lastChallengeFlashSeq = -1;

  function emitChallengeChoice(accepted: boolean): void {
    for (const handler of challengeChoiceHandlers) handler(accepted);
  }

  function isTouchscreenMode(): boolean {
    return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  }

  challengeYesBtn?.addEventListener("click", () => emitChallengeChoice(true));
  challengeNoBtn?.addEventListener("click", () => emitChallengeChoice(false));
  document.addEventListener("mousedown", (evt) => {
    if (!challengeDialog || challengeDialog.hidden) return;
    if (isTouchscreenMode()) return;
    // While the challenge prompt is open, treat mouse click (same input used
    // for peck) as selecting the currently highlighted option.
    evt.preventDefault();
    const active = document.activeElement;
    if (active === challengeNoBtn) emitChallengeChoice(false);
    else emitChallengeChoice(true);
  });
  const hideChallengeCompleteDialog = (): void => {
    if (!challengeCompleteDialog) return;
    challengeCompleteDialog.hidden = true;
  };
  challengeCompleteOkBtn?.addEventListener("click", hideChallengeCompleteDialog);
  document.addEventListener("mousedown", () => {
    if (!challengeCompleteDialog || challengeCompleteDialog.hidden) return;
    hideChallengeCompleteDialog();
  });
  document.addEventListener("keydown", (evt) => {
    if (!challengeDialog || challengeDialog.hidden) return;
    if (evt.key === "y" || evt.key === "Y") {
      evt.preventDefault();
      emitChallengeChoice(true);
      return;
    }
    if (evt.key === "n" || evt.key === "N") {
      evt.preventDefault();
      emitChallengeChoice(false);
    }
  });
  document.addEventListener("keydown", (evt) => {
    if (!challengeCompleteDialog || challengeCompleteDialog.hidden) return;
    if (evt.key === "Enter" || evt.key === "Escape" || evt.key === " ") {
      evt.preventDefault();
      hideChallengeCompleteDialog();
    }
  });

  return {
    setScreen,
    getScreen: () => currentScreen,

    setPointerLocked(locked) {
      if (locked) body.dataset["pointerLocked"] = "";
      else delete body.dataset["pointerLocked"];
    },

    updateGameState(s) {
      setBind("score", s.score);
      setBind("hunger", s.hunger);
      setBind("stamina", s.stamina);
      setBind("level", s.level);
      setBind("time", `${Math.floor(s.elapsedMs / 1000)}s`);
      setBind("crosshair", s.airborne ? "↑" : "·");

      if (hudEventRoot && hudEventText) {
        const hasEvent = s.lastEvent.length > 0;
        hudEventRoot.hidden = !hasEvent;
        hudEventText.textContent = s.lastEvent;
        if (!hasEvent) hudEventPanel?.classList.remove("hud-event--playing");
        if (hudEventThumb) {
          if (hasEvent && s.lastEventFood) {
            hudEventThumb.hidden = false;
            hudEventThumb.alt = s.lastEventFood.name;
            hudEventThumb.src = getFoodPreviewDataUrl(s.lastEventFood);
          } else if (hasEvent && s.lastEventIcon) {
            hudEventThumb.hidden = false;
            hudEventThumb.alt = "dialog icon";
            hudEventThumb.src = getIconPreviewDataUrl(s.lastEventIcon);
          } else {
            hudEventThumb.hidden = true;
            hudEventThumb.removeAttribute("src");
            hudEventThumb.alt = "";
          }
        }
        if (hudEventPanel && s.eventBannerSeq !== lastEventBannerSeq) {
          lastEventBannerSeq = s.eventBannerSeq;
          hudEventPanel.classList.remove("hud-event--playing");
          if (hasEvent && s.lastEventFood && !s.eventBannerPinned) {
            void hudEventPanel.offsetWidth;
            hudEventPanel.classList.add("hud-event--playing");
          }
        }
      }
      if (hudChallenge && hudChallengeTitle && hudChallengeProgress) {
        hudChallenge.hidden = !s.challengeActive;
        hudChallengeTitle.textContent = s.challengeTitle;
        if (s.challengeChecklist) {
          hudChallengeProgress.hidden = true;
          if (hudChallengeChecklist) {
            hudChallengeChecklist.hidden = false;
          }
          if (hudChallengeItem1) {
            hudChallengeItem1.textContent = s.challengeChecklist.item1Label;
            hudChallengeItem1.classList.toggle("done", s.challengeChecklist.item1Done);
          }
          if (hudChallengeItem2) {
            if (s.challengeChecklist.item2Label) {
              hudChallengeItem2.hidden = false;
              hudChallengeItem2.textContent = s.challengeChecklist.item2Label;
              hudChallengeItem2.classList.toggle("done", Boolean(s.challengeChecklist.item2Done));
            } else {
              hudChallengeItem2.hidden = true;
              hudChallengeItem2.classList.remove("done");
            }
          }
        } else {
          hudChallengeProgress.hidden = false;
          hudChallengeProgress.textContent = `Breadcrumbs ${s.challengeProgress}/${s.challengeGoal}`;
          if (hudChallengeChecklist) hudChallengeChecklist.hidden = true;
          if (hudChallengeItem2) hudChallengeItem2.hidden = false;
        }
      }
      if (challengeCompleteDialog && s.challengeFlashSeq !== lastChallengeFlashSeq) {
        lastChallengeFlashSeq = s.challengeFlashSeq;
        if (s.challengeFlashSeq > 0) {
          if (challengeCompleteThumb) {
            challengeCompleteThumb.src = getFoodPreviewDataUrl(s.challengeCompletionFood);
            challengeCompleteThumb.alt = s.challengeCompletionFood.name;
          }
          const completeText = document.getElementById("challenge-complete-text");
          if (completeText) {
            completeText.textContent = `+${s.challengeCompletionPoints} score and hunger fully replenished.`;
          }
          challengeCompleteDialog.hidden = false;
          const panel = challengeCompleteDialog.querySelector(".challenge-complete-dialog__panel");
          if (panel instanceof HTMLElement) {
            panel.style.animation = "none";
            void panel.offsetWidth;
            panel.style.animation = "";
          }
          challengeCompleteOkBtn?.focus();
        }
      }
      const staminaBars = binds["stamina"];
      if (staminaBars) {
        for (const el of staminaBars) {
          if (el instanceof HTMLProgressElement && el.max !== s.staminaMax) {
            el.max = s.staminaMax;
          }
        }
      }
    },

    showRunSummary(summary) {
      setBind("final-score", summary.score);
      setBind("final-duration", `${Math.floor(summary.durationMs / 1000)}s`);
      setBind("final-items", summary.itemsCollected);
      setBind("reason", summary.reason);
    },

    setSubmitStatus(status) {
      const btn = $("#submit-btn") as HTMLButtonElement | null;
      const err = $("#submit-error");
      const ok = $("#submit-ok");
      const form = $("#submit-form") as HTMLFormElement | null;
      if (!btn || !err || !ok || !form) return;
      err.hidden = status !== "error";
      ok.hidden = status !== "ok";
      btn.disabled = status === "submitting";
      form.hidden = status === "ok";
      btn.textContent = status === "submitting" ? "Submitting..." : "Submit to leaderboard";
    },

    getPlayerName() {
      const raw = playerInput?.value?.trim() ?? "";
      return raw.length > 0 ? raw : "pigeon";
    },

    setPlayerName(name) {
      if (playerInput) playerInput.value = name;
    },

    showChallengeDialog(show, kind = "grandma") {
      if (!challengeDialog) return;
      challengeDialog.hidden = !show;
      if (show) {
        if (challengeDialogThumb) {
          if (kind === "donutHeist") {
            challengeDialogThumb.src = getFoodPreviewDataUrl({ id: "donut", color: 0xf0aed4 });
            challengeDialogThumb.alt = "Donut";
          } else if (kind === "polly") {
            challengeDialogThumb.src = getIconPreviewDataUrl("🦜");
            challengeDialogThumb.alt = "Polly";
          } else {
            challengeDialogThumb.src = getIconPreviewDataUrl("👩‍🦳");
            challengeDialogThumb.alt = "Grandma";
          }
        }
        challengeYesBtn?.focus();
      }
    },

    onChallengeChoice(handler) {
      challengeChoiceHandlers.push(handler);
    },

    onAction(handler) { actionHandlers.push(handler); },
    onSubmit(handler) { submitHandlers.push(handler); },
  };
}

const previewCache = new Map<string, string>();
const iconPreviewCache = new Map<string, string>();

function getFoodPreviewDataUrl(food: { id: string; color: number }): string {
  const key = `${food.id}:${food.color}`;
  let url = previewCache.get(key);
  if (!url) {
    url = drawFoodPreviewDataUrl(food);
    previewCache.set(key, url);
  }
  return url;
}

function getIconPreviewDataUrl(icon: string): string {
  let url = iconPreviewCache.get(icon);
  if (!url) {
    const size = 48;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "";
    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = "rgba(0,0,0,0.32)";
    roundRect(ctx, 2, 2, size - 4, size - 4, 6);
    ctx.fill();
    ctx.font = "30px Apple Color Emoji, Segoe UI Emoji, Noto Color Emoji, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(icon, size / 2, size / 2 + 1);
    url = canvas.toDataURL("image/png");
    iconPreviewCache.set(icon, url);
  }
  return url;
}

function drawFoodPreviewDataUrl(food: { id: string; color: number }): string {
  const size = 48;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  const hex = `#${food.color.toString(16).padStart(6, "0")}`;
  const cx = size / 2;
  const cy = size / 2;
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = hex;
  ctx.strokeStyle = "rgba(0,0,0,0.35)";
  ctx.lineWidth = 1;

  switch (food.id) {
    case "breadcrumb": {
      ctx.beginPath();
      ctx.arc(cx, cy, 11, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      break;
    }
    case "fry": {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-0.35);
      roundRect(ctx, -14, -3, 28, 6, 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      break;
    }
    case "crust": {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(0.15);
      roundRect(ctx, -16, -4, 32, 8, 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      break;
    }
    case "donut": {
      ctx.beginPath();
      ctx.arc(cx, cy, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.arc(cx, cy, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalCompositeOperation = "source-over";
      break;
    }
    case "hotdog": {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(0.2);
      roundRect(ctx, -5, -16, 10, 32, 4);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
      break;
    }
    case "popcorn": {
      // Movie-style popcorn box.
      ctx.save();
      ctx.translate(cx, cy + 2);
      roundRect(ctx, -11, -13, 22, 24, 3);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,0.92)";
      roundRect(ctx, -6, -12, 3, 22, 1.2);
      roundRect(ctx, -1.5, -12, 3, 22, 1.2);
      roundRect(ctx, 3, -12, 3, 22, 1.2);
      ctx.fill();
      ctx.fillStyle = "#f3e38f";
      ctx.strokeStyle = "rgba(0,0,0,0.25)";
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.arc(i * 4, -15 - Math.abs(i % 2), 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
      ctx.restore();
      break;
    }
    default: {
      ctx.beginPath();
      ctx.arc(cx, cy, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }

  return canvas.toDataURL("image/png");
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}
